import { SignageNativeConfig, CacheableResource, CacheConf, Screen } from 'common/types'
import { sleep } from './utils'
import { getLogger } from './logger'
const log = getLogger('native-bridge')

/**
 * Native bridge for platform-specific functionality.
 *
 * Platforms:
 * - Desktop (macOS/Windows/Linux): window.NativeBridge (Tauri proxy injection)
 * - Android: window.NativeBridge (addJavascriptInterface as "NativeBridge" + onPageStarted property injection)
 * - iOS/iPadOS: window.NativeBridge (WKUserScript injection, IPC over the local caching proxy)
 * - WebOS: window.NativeBridge (injected into iframe contentWindow by parent app)
 * - Tizen: window.NativeBridge (injected by parent app)
 * - Expo (legacy): window.signageNativeConfig (injected)
 * - Web: no native host
 */

/**
 * Check if running in a native app (has a native host).
 */
export function isNativeApp(): boolean {
  return !!window.NativeBridge || !!window.ReactNativeWebView
}

// CDN domains whose URLs are rewritten to go through the Rust proxy so they
// are cached in cacache for offline playback.
const NATIVE_MEDIA_DOMAINS = ['imagedelivery.net', 'cloudflarestream.com', 'cloudinary.com']

// Platforms where media URLs must be rewritten to flow through a local proxy.
// Android is excluded because OkHttp intercepts all network requests at the OS
// level and handles caching without URL rewriting. iOS is included: WKWebView
// has no request-interception hook, so the iOS player serves the app from a
// local proxy the same way the desktop players do.
const DESKTOP_PLATFORMS = new Set<NativeBridge['platform']>(['macos', 'windows', 'linux', 'ios', 'webos', 'tizen'])

/**
 * Rewrite an absolute CDN media URL to go through the local Rust proxy
 * (`/_native/media/<host><path>`) when running on a desktop player.
 *
 * This makes every image and video request flow through the proxy, which
 * caches responses in cacache for offline playback. No-op on Android
 * (OkHttp handles it) and in the browser (no proxy).
 */
export function getNativeMediaUrl(url: string): string {
  const platform = window.NativeBridge?.platform
  if (!platform || !DESKTOP_PLATFORMS.has(platform)) return url
  // Only rewrite when the page is actually served through a local caching
  // proxy (desktop Rust server, WebOS Luna service, or a SW proxy at
  // /__player__/). In the no-SW fallback (Tizen/WebOS) the player is loaded
  // straight from the upstream origin, where /_native/media/* doesn't exist —
  // rewriting there breaks all image/video playback.
  const { hostname, pathname } = window.location
  const behindLocalProxy = hostname === '127.0.0.1' || hostname === 'localhost' || pathname.startsWith('/__player__')
  if (!behindLocalProxy) return url
  try {
    const parsed = new URL(url)
    if (NATIVE_MEDIA_DOMAINS.some((d) => parsed.hostname.endsWith(d))) {
      return `/_native/media/${parsed.hostname}${parsed.pathname}${parsed.search}`
    }
  } catch {
    // not a valid absolute URL — return as-is
  }
  return url
}

/**
 * Get native config from the host app.
 * For Expo, waits briefly for config injection.
 */
export async function getNativeConfig(): Promise<SignageNativeConfig | null> {
  // Desktop + Android bridge - available immediately
  if (window.NativeBridge) {
    try {
      return JSON.parse(window.NativeBridge.getConfig())
    } catch {
      return null
    }
  }

  // Expo injects config via postMessage (async)
  if (window.ReactNativeWebView) {
    await sleep(100) // wait briefly for injection
    return window.signageNativeConfig ?? null
  }
  return null
}

// Caching rules — these are sent to the native layer as part of CacheConf.
// cacheFirstRules: immutable content (hash-busted assets, CDN media segments)
// noCacheRules:   never cache (SSE streams, etc.)
const CACHE_FIRST_RULES = [
  '/assets/[A-Za-z0-9_-]{8}\\.(js|css)$', // hash-busted JS/CSS bundles
  'cloudflarestream\\.com/.*/video/', // DASH video segments
  'imagedelivery\\.net/.*/public(hd|4k)', // Cloudflare Images (high-res / 4K)
]
const NO_CACHE_RULES = ['/sse']

const DEFAULT_CACHE_CONF: CacheConf = {
  mode: 'warm',
  cacheFirstRules: CACHE_FIRST_RULES,
  noCacheRules: NO_CACHE_RULES,
}

/**
 * Send a full CacheConf object to the native layer.
 * Prefer `syncCacheConf()` for standard use cases.
 */
export function setCacheConf(conf: CacheConf): void {
  log('Setting cache conf:', conf)
  window.NativeBridge?.setCacheConf?.(JSON.stringify(conf))
}

/**
 * Sync cache mode to native using the standard rule set.
 * Call this when screen.localPlayback changes.
 */
export function syncCacheConf(mode: CacheConf['mode']): void {
  setCacheConf({
    ...DEFAULT_CACHE_CONF,
    mode,
  })
}

/**
 * Get cache statistics (for debugging).
 */
export function getCacheStats(): string | null {
  return window.NativeBridge?.getCacheStats?.() ?? null
}

/**
 * Clear the native cache.
 */
export function clearNativeCache(): void {
  window.NativeBridge?.clearCache?.()
}

/**
 * Clear the service worker cache.
 * Works in any browser with service worker support.
 */
export async function clearServiceWorkerCache(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false
  }

  const registration = await navigator.serviceWorker.ready
  const sw = registration.active
  if (!sw) {
    return false
  }

  return new Promise((resolve) => {
    const channel = new MessageChannel()
    channel.port1.onmessage = (event) => {
      resolve(event.data?.success ?? false)
    }
    sw.postMessage({ type: 'CLEAR_CACHE' }, [channel.port2])
  })
}

/**
 * Clear all caches (both native and service worker).
 */
export async function clearAllCaches(): Promise<void> {
  clearNativeCache()
  await clearServiceWorkerCache()
}

/**
 * Unregister the service worker.
 * Use this to disable SW caching if users experience stale asset issues.
 * Returns true if SW was unregistered, false if no SW was registered.
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  await clearServiceWorkerCache()

  if (!('serviceWorker' in navigator)) {
    return false
  }

  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) {
    return false
  }

  const success = await registration.unregister()
  if (success) {
    console.log('[SW] Unregistered')
  }
  return success
}

/**
 * Register the service worker.
 * Use this to re-enable SW caching after it was disabled.
 */
export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js')
    console.log('[SW] Registered:', registration.scope)
  } catch (error) {
    console.error('[SW] Registration failed:', error)
  }
}

/**
 * Check if service worker is currently registered.
 */
export async function isServiceWorkerRegistered(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false
  }
  const registration = await navigator.serviceWorker.getRegistration()
  return !!registration
}

/**
 * Force check for service worker updates.
 */
export function checkForServiceWorkerUpdates(): void {
  if (!('serviceWorker' in navigator)) {
    return
  }
  navigator.serviceWorker.getRegistration().then((registration) => {
    registration?.update()
  })
}

/**
 * Get cache entries currently present in native cache storage.
 */
export function getCacheEntries(filter?: string): string | null {
  return window.NativeBridge?.getCacheEntries?.(filter) ?? null
}

/**
 * Get cache coverage status for configured cache targets.
 */
export function getCacheTargetsStatus(): string | null {
  return window.NativeBridge?.getCacheTargetsStatus?.() ?? null
}

/**
 * Get list of cache misses (requests that couldn't be served from cache).
 */
export function getCacheMisses(): string | null {
  return window.NativeBridge?.getCacheMisses?.() ?? null
}

/**
 * Send screen info to the native layer for the settings menu.
 */
export function setScreenInfo(screen: Screen): void {
  if (!window.NativeBridge?.setScreenInfo) return
  try {
    const { name, orientation, screenType, syncronized, localPlayback, playlist, channel } = screen
    const info = {
      name,
      orientation,
      screenType,
      syncronized,
      localPlayback,
      playlistName: playlist?.name,
      channelName: channel?.name,
    }
    window.NativeBridge.setScreenInfo(JSON.stringify(info))
  } catch (e) {
    console.error('Failed to set screen info:', e)
  }
}

// Internal store of cacheable resources for updates from VideoPlayer
let cacheableResources: CacheableResource[] = []

function sendCacheableResourcesToNative(): void {
  if (!window.NativeBridge?.setCacheTargets) return
  try {
    window.NativeBridge.setCacheTargets(JSON.stringify(cacheableResources))
  } catch (e) {
    console.error('Failed to set cacheable resources:', e)
  }
}

/**
 * Set cache targets for tracking caching progress and debug visibility.
 * Only supported on native hosts that implement NativeBridge.setCacheTargets.
 */
export function setCacheTargets(resources: CacheableResource[]): void {
  cacheableResources = resources
  sendCacheableResourcesToNative()
}

/**
 * Update a specific video resource with resolved segment URLs.
 * Called by VideoPlayer when Shaka resolves the actual segment URLs for a DASH video.
 * Only supported on Android.
 */
export function updateCacheableVideoUrls(contentId: string, segmentUrls: string[]): void {
  const resource = cacheableResources.find((r) => r.id === contentId)
  if (resource) {
    resource.urls = segmentUrls
    sendCacheableResourcesToNative()
  }
}
